<template>
  <v-dialog v-model="valueLocal" max-width="480" persistent>
    <ng-sheet class="checkout-dialog">
      <h2>{{ $t('product.checkout') }}</h2>

      <form @submit.prevent>
        <v-btn-toggle
          v-if="$i18n.locale === 'ru' && !isLegalEntityOrderDisabled"
          class="mb-4 d-flex"
          v-model="isLegalEntity"
          name="isLegalEntity"
          color="primary"
          mandatory
        >
          <v-btn class="px-2" text :value="false">
            <span class="d-none d-sm-inline">Физическое</span>
            <span class="d-inline d-sm-none">Физ.</span>&nbsp;лицо
          </v-btn>
          <v-btn class="px-2" text :value="true">
            <span class="d-none d-sm-inline">Юридическое</span>
            <span class="d-inline d-sm-none">Юр.</span>&nbsp;лицо
          </v-btn>
        </v-btn-toggle>
        <v-alert class="mt-3 mb-6 pr-6" color="primary" v-if="isLegalEntity" outlined dense border="left">
          <small class="text-base">
            <strong>Внимание:</strong>
            <template v-if="finalTotalPrice.amount < 5000">
              Для заказов менее 5&nbsp;000&nbsp;₽&nbsp;мы&nbsp;добавим к&nbsp;стоимости 3&nbsp;000&nbsp;₽.<br />
            </template>
            Если понадобится двусторонний договор, то&nbsp;к&nbsp;стоимости
            <span v-if="finalTotalPrice.amount < 5000">любого</span> заказа мы&nbsp;добавим 5&nbsp;000&nbsp;₽.
          </small>

          <v-hover>
            <template #default="{ hover }">
              <a href="/ru/faq/#pricebywire" class="nolink" target="_blank">
                <v-icon class="checkout-dialog__entity-help-link" dense :color="hover ? 'primary' : ''">
                  mdi-help-circle
                </v-icon>
              </a>
            </template>
          </v-hover>
        </v-alert>
        <v-file-input
          v-if="isLegalEntity"
          outlined
          dense
          v-model="entityCard"
          prepend-icon=""
          prepend-inner-icon="mdi-paperclip"
          placeholder="Приложите карточку организации"
          hide-details="auto"
          class="mb-4 text-body-2"
          :error="!!vuelidateErrors.entityCard.length"
          :error-messages="vuelidateErrors.entityCard"
          :disabled="isLoading"
        ></v-file-input>
        <ng-text-area
          v-if="isLegalEntity"
          label="Комментарий к заказу"
          name="comment"
          rows="3"
          v-model="comment"
          placeholder="Перечислите все нужные регионы, если их несколько. Укажите, нужен ли договор."
          class="mb-4"
          small
          :disabled="isLoading"
        ></ng-text-area>
        <div class="mb-4">
          <!-- eslint-disable vuejs-accessibility/no-autofocus -->
          <ng-text-field
            :label="`${$t('app.yourEmail')}:`"
            placeholder="address@example.com"
            v-model="email"
            name="email"
            :error="!!vuelidateErrors.email.length"
            :error-messages="vuelidateErrors.email"
            :disabled="isLoading"
            type="email"
            autocomplete="on"
            autofocus
            small
            @input.once="onBeginCheckout"
          />
          <!-- eslint-enable -->
          <div class="small text-muted mt-2">{{ emailHint }}</div>
        </div>
        <ng-sheet class="mt-4 mb-3" color="secondary">
          <div class="small">
            <h4 class="mb-3">
              <v-icon style="margin: -4px 2px 0px -2px">mdi-invoice-list-outline</v-icon>
              {{ $t('order.checkDetails') }}
            </h4>
            <div v-html="summaryDetailed"></div>
            <div>
              {{ $t('product.price') }}: <span v-if="isLegalEntity">от&nbsp;</span
              ><ng-price
                v-if="finalTotalPrice"
                class="d-inline-block h4 my-0"
                :amount="finalTotalPrice.amount"
                :currency="finalTotalPrice.currency"
              />
              <ng-price
                v-if="totalPrice && totalPrice !== finalTotalPrice"
                class="d-inline-block text-muted ml-2"
                simple
                is-crossed
                :amount="totalPrice.amount"
                :currency="totalPrice.currency"
              />
            </div>
          </div>
        </ng-sheet>
        <ng-sheet outlined class="py-3" v-if="!isLegalEntity">
          <ng-collapsible class="checkout-promocode mt-n1" activator-as-link :collapsed="!orderCoupon">
            <template slot="activator">
              <span class="checkout-promocode__link">
                <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
                <span class="small" @click="onUsePromoCodeClick">{{ $t('order.usePromoCode') }}</span>
                <v-tooltip
                  class="cursor-pointer"
                  right
                  :value="isTooltipShown"
                  content-class="checkout-promocode__tooltip"
                >
                  <template v-slot:activator="{ attrs }">
                    <v-icon
                      small
                      :class="['checkout-promocode__link-help ng-fieldset__help-icon ml-2', { active: isTooltipShown }]"
                      v-bind="attrs"
                      @click.stop="isTooltipShown = !isTooltipShown"
                      >mdi-help-circle</v-icon
                    >
                  </template>
                  <div class="dark pa-1" style="max-width: 250px" @click.stop v-html="$t('order.promocodeHint')"></div>
                </v-tooltip>
              </span>
            </template>

            <template slot="content">
              <ng-text-field
                outlined
                class="mt-3 my-2"
                :placeholder="$t('order.enterPromoCodeHere')"
                name="coupon"
                type="text"
                small
                v-model="couponLocal"
                :disabled="isLoading"
                :error="orderCouponStatus === 'failed'"
                :error-messages="orderCouponError && [orderCouponError]"
                :success="orderCouponStatus === 'success' && !isCouponInputDirty"
                autocomplete="off"
                @input="onCouponInput"
                clearable
              >
                <template v-slot:append-outer>
                  <ng-button
                    width="100"
                    v-if="orderCouponStatus === 'success' && !isCouponInputDirty"
                    color="success"
                    depressed
                    >{{ $t('order.applied') }}</ng-button
                  >
                  <ng-button
                    width="100"
                    v-else
                    bordered
                    depressed
                    @click="applyCoupon"
                    :loading="orderCouponStatus === 'loading'"
                  >
                    {{ $t('order.apply') }}
                  </ng-button>
                </template>
              </ng-text-field>
              <div class="small mt-2" v-if="orderCouponStatus === 'success' && discount">
                🔥 {{ $t('order.promoCodeWasApplied') }} <strong>{{ discount }}% </strong>
              </div>
            </template>
          </ng-collapsible>
        </ng-sheet>
        <ng-button class="mt-6" color="primary" x-large block @click="buyProduct" :loading="isLoading">
          {{ submitButtonText }}
        </ng-button>
      </form>
      <ng-button
        class="checkout-dialog__close"
        @click="closeDialog"
        text
        icon
        icon-name="mdi-close"
        :disabled="isLoading"
      />
    </ng-sheet>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { validationMixin } from 'vuelidate';
import { email, required, requiredIf } from 'vuelidate/lib/validators';
import vuelidatable from '@ngservices_front/mixins/vuelidatable';
import NgCollapsible from '@/components/ui/NgCollapsible.vue';
import productService from '@/services/product/productService';
import * as promotionService from '@/services/promotionService';
import orderService from '@/services/orderService';
import gtagService from '@/services/loggers/gtagService';

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [validationMixin, vuelidatable],
  components: { NgCollapsible },
  data() {
    return {
      email: undefined,
      isLegalEntity: false,
      comment: undefined,
      entityCard: undefined,
      legalEntityCardId: null,
      couponLocal: undefined,
      isTooltipShown: false,
      isOrderCreating: false,
      isCouponInputDirty: false,
    };
  },
  computed: {
    ...mapState('order', { orderEmail: 'email' }),
    ...mapState('order', ['totalPrice', 'discount']),
    ...mapState('promotions', ['orderCoupon', 'orderCouponStatus', 'orderCouponError']),
    ...mapState('app', ['isLegalEntityOrderDisabled']),
    ...mapGetters('map', ['drawingMode']),
    ...mapGetters('order', ['finalTotalPrice']),
    ...mapGetters('product', ['productDataFormatted']),
    ...mapGetters('regions', ['currentRegionName']),
    valueLocal: {
      get() {
        return this.value;
      },
      set(val) {
        if (!val) this.hidePromocodeTooltip();
        this.$emit('input', val);
      },
    },
    summaryDetailed() {
      return productService.getSummaryDetailed(this.currentRegionName, this.productDataFormatted);
    },
    emailHint() {
      return this.isLegalEntity ? 'Мы пришлем счёт на этот адрес' : this.$t('product.emailHint');
    },
    submitButtonText() {
      if (this.finalTotalPrice?.amount === 0) return this.$t('product.order');
      return this.isLegalEntity ? this.$t('product.issueInvoice') : this.$t('product.purchase');
    },
    isLoading() {
      return this.orderCouponStatus === 'loading' || this.isOrderCreating;
    },
  },
  watch: {
    async entityCard(val) {
      if (val) {
        try {
          this.isOrderCreating = true;
          const { card_id } = await orderService.registerEntityCard(val);
          this.legalEntityCardId = card_id;
        } catch (e) {
          this.entityCard = null;
        } finally {
          this.isOrderCreating = false;
        }
      } else {
        this.legalEntityCardId = null;
      }
    },
    isTooltipShown(val) {
      if (val) {
        if (window.ym) window.ym(42050389, 'reachGoal', 'use-promocode-help');
        document.body.addEventListener('click', this.hidePromocodeTooltip);
      }
      if (!val) {
        document.body.removeEventListener('click', this.hidePromocodeTooltip);
      }
    },
    value: {
      handler(newValue) {
        if (newValue) {
          orderService.setInitialData();
          this.couponLocal = this.orderCoupon;

          this.email = this.orderEmail;
          if (this.email) this.onBeginCheckout();
        }
      },
      immediate: true,
    },
    isLegalEntity: {
      handler(newIsLegalEntity) {
        this.SET_IS_LEGAL_ENTITY(newIsLegalEntity);
      },
      immediate: true,
    },
  },
  validations: {
    email: { email, required },
    entityCard: {
      required: requiredIf(function () {
        return this.isLegalEntity;
      }),
    },
  },
  beforeDestroy() {
    orderService.resetTotalPrice();
  },
  methods: {
    ...mapMutations('order', ['SET_ORDER_EMAIL', 'SET_IS_LEGAL_ENTITY']),
    ...mapMutations('promotions', ['SET_ORDER_COUPON', 'SET_ORDER_COUPON_STATUS', 'SET_ORDER_COUPON_ERROR']),
    async buyProduct() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          this.isOrderCreating = true;
          const orderData = {
            product: this.productDataFormatted,
            email: this.email,
            comment: this.isLegalEntity ? this.comment : '',
            customerType: this.isLegalEntity ? 'legal' : 'physical',
            legalEntityCardId: this.legalEntityCardId,
            coupon: !this.isLegalEntity && this.orderCoupon ? this.orderCoupon.trim().toLowerCase() : null,
          };

          if (window.ym)
            window.ym(42050389, 'reachGoal', 'paypal', {
              product: this.productDataFormatted,
              drawingMode: this.drawingMode,
            });
          const vectorFormat =
            this.productDataFormatted.options.format || this.productDataFormatted.options.format_vector;
          if (window.ym) {
            if (vectorFormat === 'shape') window.ym(42050389, 'reachGoal', 'buy-shape');
            if (vectorFormat === 'gpkg') window.ym(42050389, 'reachGoal', 'buy-gpkg');
          }

          const { payment_strategy, paymentSystemUrl } = await orderService.buyDataProduct(orderData);
          this.closeDialog();

          if (payment_strategy === 'free') this.$emit('success:freeExtraction');
          if (payment_strategy === 'stripe' || payment_strategy === 'yoomoney') {
            if (this.isLegalEntity) {
              this.$emit('success:invoice');
            } else {
              this.$emit('success:payment');
              window.location.href = paymentSystemUrl;
            }
          }
        } finally {
          this.isOrderCreating = false;
        }
      }
    },
    async applyCoupon() {
      this.isCouponInputDirty = false;
      if (this.couponLocal || (!this.couponLocal && this.orderCoupon)) {
        await orderService.applyCoupon({
          product: this.productDataFormatted,
          coupon: this.couponLocal?.trim().toLowerCase(),
        });
      } else {
        promotionService.setOrderCouponToStore({ coupon: this.couponLocal, couponStatus: 'no_coupon' });
      }
    },
    onCouponInput() {
      if (this.orderCouponStatus !== 'success' && this.orderCouponStatus !== 'iddle') {
        orderService.resetCoupon({ shouldApplyCoupon: true });
      }
      this.isCouponInputDirty = true;
    },
    async resetCoupon({ shouldApplyCoupon } = { shouldApplyCoupon: false }) {
      this.couponLocal = undefined;
      // dont apply coupon when reset called during closing dialog
      if (shouldApplyCoupon) await this.applyCoupon();
      orderService.resetCoupon();
    },
    closeDialog() {
      this.valueLocal = false;
      this.resetCoupon();
      this.isCouponInputDirty = false;
    },
    onUsePromoCodeClick() {
      if (window.ym) window.ym(42050389, 'reachGoal', 'use-promocode');
    },
    hidePromocodeTooltip() {
      this.isTooltipShown = false;
    },
    onBeginCheckout() {
      gtagService.fireEcommerceEvent({
        eventName: 'begin_checkout',
        eventData: {
          value: this.finalTotalPrice.amount,
          currency: this.finalTotalPrice.currency,
          items: [
            gtagService.createEcommerceItem({
              productItem: this.productDataFormatted,
              price: this.finalTotalPrice,
            }),
          ],
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.checkout-dialog {
  position: relative;

  &__close {
    position: absolute;
    right: 8px;
    top: 8px;
  }

  &__entity-help-link {
    position: absolute;
    top: 8px;
    right: 8px;
  }
  &::v-deep .v-input__append-outer {
    align-self: start;
  }
}

.checkout-promocode {
  &__link {
    position: relative;
  }
  &__link-help {
    position: absolute;
    right: -22px;
    top: 50%;
    margin-top: -7px;

    &:hover,
    &.active {
      color: $primary;
    }
  }
  &__tooltip {
    pointer-events: all;
  }
}
</style>
<style lang="scss">
.v-overlay,
.v-overlay__scrim {
  backdrop-filter: blur(3px);
}
</style>
